import { FC } from 'react';
import {
  matchPath,
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from 'react-router';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import AccountHubPage from './AccountHubPage';
import AccountHubEmptyState from './AccountHubEmptyState';
import { RENTER_PATHS } from 'src/routes/renterPaths';

/**
 * Two-pane layout for desktop Account Hub.
 *
 * Desktop (md+):
 *   ┌─────────────────┬────────────────────────────┐
 *   │  Menu / Index   │  Child route content        │
 *   │  (1/3 width)    │  (2/3 width)                │
 *   └─────────────────┴────────────────────────────┘
 *
 * Mobile:
 *   - At the index URL (with or without userId): renders AccountHubPage full-screen
 *   - At any child route: renders only the child (Outlet) full-screen
 */
const AccountHubLayout: FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const location = useLocation();
  const { userId } = useParams<{ userId: string }>();

  const hubBase = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}`;

  // We're "at index" when:
  //   (a) there is no userId param — user is at /account-hub
  //   (b) userId is present and the path exactly matches /account-hub/:userId
  const isAtIndex =
    !userId ||
    matchPath(
      { path: `${hubBase}/${userId}`, end: true },
      location.pathname
    ) !== null;

  if (isDesktop) {
    // On desktop, if a userId is selected but no sub-page is active, default
    // to login-security so the right pane is never blank.
    const defaultContent =
      isAtIndex && userId ? (
        <Navigate
          to={`${hubBase}/${userId}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}`}
          replace
        />
      ) : isAtIndex ? (
        <AccountHubEmptyState />
      ) : (
        <Outlet />
      );

    return (
      <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
        {/* Menu pane — always visible on desktop */}
        <Box
          sx={{
            flex: '0 0 360px',
            maxWidth: 420,
            minWidth: 300,
            borderRight: '1px solid',
            borderColor: 'divider',
            overflowY: 'auto',
            height: '100%',
          }}
        >
          <AccountHubPage />
        </Box>

        {/* Content pane */}
        <Box sx={{ flex: 1, overflowY: 'auto', height: '100%' }}>
          {defaultContent}
        </Box>
      </Box>
    );
  }

  // Mobile: show menu at index, full-screen child on sub-routes
  return isAtIndex ? <AccountHubPage /> : <Outlet />;
};

export default AccountHubLayout;
